import type { FC } from 'react'

import { LoaderCircle, Table } from '@skytech/ui'
import { Th } from '@skytech/ui/Table/Th'
import { getQueryFilter, getSortedList } from '@skytech/utils'
import { useQuery } from '@tanstack/react-query'

import { useAppContext } from '@/app/index.context'
import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { getQueryFilterByCustomerId } from '@/components/CustomerSelect'
import { NoResults } from '@/components/NoResults'
import { ResultCount } from '@/components/ResultCount'
import { useAdminAccess } from '@/hooks/access'
import { useSearchParamsByKey, useSortParams } from '@/hooks/searchParams'
import { useFormattedNumber } from '@/hooks/use-formatted-number'
import { useRenderLimit } from '@/hooks/use-render-limit'
import type { SalaryDeductionProfile } from '@/modules/admin/resources'
import adminResources from '@/modules/admin/resources'

import queries from '../queries'

import { TelePoliciesListItem } from './TelePoliciesListItem'

export const TelePoliciesList: FC<{
  searchFilter: string

  onClick: ({ telePolicyId, isOpen }: { telePolicyId: number; isOpen: boolean }) => void
}> = ({ searchFilter, onClick }) => {
  useAdminAccess()

  const translate = useTranslation()
  const { formatNumber } = useFormattedNumber()
  const [queryCustomer] = useSearchParamsByKey('company')
  const [{ querySort, querySortBy }, { handleSort }] =
    useSortParams<keyof (typeof telePolicyParsedForSorting)[number]>()
  const { locale } = useAppContext()

  const {
    data: addressList,
    isLoading,
    isError,
  } = useQuery(queries.adminTelePoliciesList(), adminResources.getTelePolicyList)

  const stringMatcher = (item: SalaryDeductionProfile) => [
    item.name,
    item.telePolicyTemplate?.name,
    item.freeAllowanceAmount,
    formatNumber({
      amount: Number(item?.subscribers),
    }),
    item.customerHead?.id ? translate('Global') : translate('Local'),
    item.customerHead?.id ? item.customerHead.name : item.customer?.name,
  ]

  const itemsFiltered =
    addressList?.filter(item => {
      const customerFilter =
        queryCustomer === 'global'
          ? item.customerHead !== null
          : getQueryFilterByCustomerId(queryCustomer, item.customer?.id)
      return getQueryFilter(searchFilter, stringMatcher) && customerFilter
    }) || []

  const getTemplateName = (item: SalaryDeductionProfile) => item.telePolicyTemplate?.name

  const telePolicyParsedForSorting = itemsFiltered.map(item => ({
    ...item,
    templateName: getTemplateName(item),
  }))

  const sortedList = getSortedList({
    list: telePolicyParsedForSorting,
    querySort,
    querySortBy,
    locale,
  })
  const { visibleList, ListLoaderComponent } = useRenderLimit(sortedList || [], 30)

  if (isLoading) {
    return <LoaderCircle />
  }

  if (isError || itemsFiltered.length === 0) {
    return <NoResults />
  }

  return (
    <>
      <ResultCount count={itemsFiltered.length} />
      <Table rowHeight="short">
        <thead>
          <tr>
            <Th id="name" onClick={handleSort}>
              <Translation id="name" />
            </Th>

            <Th id="templateName" onClick={handleSort}>
              <Translation id="template" />
            </Th>

            <Th id="customerHead" onClick={handleSort}>
              <Translation id="Type" />
            </Th>

            <Th className="text-right" id="freeAllowanceAmount" onClick={handleSort}>
              <Translation id="Free allowance amount" />
            </Th>

            <Th className="text-right" id="subscribers" onClick={handleSort}>
              <Translation id="Subscribers" />
            </Th>
          </tr>
        </thead>
        <tbody>
          {visibleList.map(item => (
            <TelePoliciesListItem
              key={item.id}
              searchFilter={searchFilter}
              onEdit={() => onClick({ telePolicyId: item.id, isOpen: true })}
              {...item}
            />
          ))}
        </tbody>
      </Table>
      <ListLoaderComponent />
    </>
  )
}
